import React from "react";
import PropTypes from "prop-types";
import { Checkbox } from "primereact/checkbox";
import { InputText, Label, Dropdown } from "../../../../components/Inputs";
import { DelphiConnectionPermissionOptions } from "../Utils";
// import { isContractor } from "../../../../components/Utils";
import { PermissionResource, Resources } from "../../../Permissions";

const getEmptyModel = () => ({
  parent_company_id: 0,
  parentCompanyName: "",
  username: "",
  password: "",
  permissions: [],
});

function generateState(pModel, kdlCompanyList = []) {
  // console.log("DelphiConnectionForm.generateState()", pModel);
  const model = pModel || {};
  let permissions = "";
  if (model.permissions) {
    const tokens = model.permissions.split(", ");
    permissions = tokens[0];
  }
  return {
    parent_company_id: model.parent_company_id || 0,
    parentCompanyName: kdlCompanyList.length > 0 ? kdlCompanyList[0].name : "",
    username: model.username || "",
    password: model.password || "",
    permissions,
    error: {
      parent_company_id: null,
      permissions: null,
    },
  };
}

function DelphiConnectionForm({
  forwardedRef,
  className,
  kdlCompanyList,
  readOnly,
  model,
  usedPermissions = [],
}) {
  const [state, setState] = React.useState(() =>
    generateState(model, kdlCompanyList),
  );

  React.useEffect(() => {
    setState(generateState(model, kdlCompanyList));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, kdlCompanyList]);

  const validate = React.useCallback(
    (initialize) => {
      const { error, ...modelState } = state;
      let isValid = true;
      const newError = { ...error };
      Object.keys(modelState).forEach((key) => {
        if (newError[key] !== undefined) {
          if (key === "permissions") {
            if (!modelState[key] || modelState[key].length === 0) {
              newError[key] = true;
              isValid = false;
            }
          } else if (modelState[key] === "") {
            newError[key] = true;
            isValid = false;
          }
        }
      });
      setState((prev) => ({ ...prev, error: newError }));
      if (!isValid) {
        return null;
      }
      if (initialize) {
        setState(getEmptyModel());
      }
      if (modelState.permissions === model.permissions) {
        setState((prev) => ({ ...prev, error: { ...prev.error, permissions: true } }));
      }
      const { id, index, status, status_id, parent_company_id } = model;
      return {
        ...modelState,
        id,
        index,
        status,
        status_id,
        oldStatusId: status_id,
        by_company_id: parent_company_id,
        company_id: parent_company_id,
      };
    },
    [state, model],
  );

  const getModel = React.useCallback(
    (initialize = false) => {
      return validate(initialize);
    },
    [validate],
  );

  React.useImperativeHandle(
    forwardedRef,
    () => ({
      getModel,
    }),
    [getModel],
  );

  const onPermissionsChange = React.useCallback((event) => {
    setState((prev) => {
      const permissions = [...prev.permissions];
      if (event.checked) {
        permissions.push(event.value);
      } else {
        permissions.splice(permissions.indexOf(event.value), 1);
      }
      return {
        ...prev,
        permissions,
        error: {
          ...prev.error,
          permissions: permissions.length === 0,
        },
      };
    });
  }, []);

  // const renderPermissions = (permModel, holderClassName = "", itemClassName = "") => {
  //   const { options, permissions } = permModel;
  //   const createCheckbox = (prefix) => (value, index) => (
  //     <div key={index} className={itemClassName}>
  //       <div className="p-col-12">
  //         <Checkbox
  //           inputId={`${prefix}${index}`}
  //           value={value}
  //           onChange={onPermissionsChange}
  //           checked={permissions.includes(value)}
  //           disabled={readOnly}
  //         />
  //         <label htmlFor={`${prefix}${index}`} className="p-checkbox-label">
  //           {value}
  //         </label>
  //       </div>
  //     </div>
  //   );
  //   const items = (options || [])
  //     .map((item) => item.value)
  //     .map(createCheckbox("delphiPermCb-"));
  //   return (
  //     <div className={holderClassName}>
  //       <Label>{`Rechte*`}</Label>
  //       <div
  //         className="w3-row neg-margin-lr"
  //         style={{ color: "#000 !important" }}
  //       >
  //         {items}
  //       </div>
  //     </div>
  //   );
  // };

  const setPermissions = React.useCallback((permissions) => {
    setState((prev) => ({
      ...prev,
      permissions,
      error: {
        ...prev.error,
        permissions: permissions === "",
      },
    }));
  }, []);

  const { parentCompanyName, username, password, permissions, error } = state;
  const KDLCompanyOptions = kdlCompanyList.map((item) => ({
    label: item.name,
    value: `${item.id}`,
  }));
  // Hide permissions already used by other delphi connections of this employee.
  // The currently selected permission stays visible so it can be edited.
  const availablePermissionOptions = DelphiConnectionPermissionOptions.filter(
    (option) =>
      !usedPermissions.includes(option.value) || option.value === permissions,
  );

  return (
    <div className={className}>
      <div
        className={`w3-col- form-group-item ${
          error.parentCompanyName ? "error-group" : ""
        }`}
      >
        <Label htmlFor="parentCompanyName">{`Vertragspartner*`}</Label>
        <Dropdown
          id="parentCompanyName"
          className="w3-block w3-border"
          value={parentCompanyName}
          placeholder="Bitte auswählen"
          options={KDLCompanyOptions}
          readOnly
        />
      </div>
      <div
        className={`w3-col- form-group-item ${
          error.permissions ? "error-group" : ""
        }`}
      >
        <Label htmlFor="permissions">{`Rechte*`}</Label>
        <Dropdown
          id="permissions"
          className="w3-block w3-border"
          value={permissions}
          placeholder="Bitte auswählen"
          options={availablePermissionOptions}
          onChange={(event) => setPermissions(event.value)}
          readOnly={readOnly}
        />
      </div>
      <div className="w3-row neg-margin-lr">
        <div className="w3-col s6 pad-lr pad-top form-group-item">
          <Label htmlFor="username">{`Kennung`}</Label>
          <InputText
            id="username"
            className="w3-block w3-border round-input pad"
            value={username}
            readonly
          />
        </div>
        <PermissionResource
          resource={Resources.Organisation.WorkerWizard.ConnectionPasswords}
        >
          <div className="w3-col s6 pad-lr pad-top form-group-item">
            <Label htmlFor="password">{`Passwort`}</Label>
            <InputText
              id="password"
              className="w3-block w3-border round-input pad"
              value={password}
              readonly
            />
          </div>
        </PermissionResource>
      </div>
    </div>
  );
}

DelphiConnectionForm.propTypes = {
  className: PropTypes.string,
  model: PropTypes.shape({
    parent_company_id: PropTypes.number,
    username: PropTypes.string,
    permissions: PropTypes.string,
  }),
  companyList: PropTypes.array,
  kdlCompanyList: PropTypes.array,
  userCompany: PropTypes.object,
  readOnly: PropTypes.bool,
  requireDescription: PropTypes.bool,
  defaultDescription: PropTypes.string,
  usedPermissions: PropTypes.array,
};

DelphiConnectionForm.defaultProps = {
  className: "",
  model: {
    parent_company_id: 0,
    username: "",
    permissions: "",
  },
  companyList: [],
  kdlCompanyList: [],
};

export default React.forwardRef((props, ref) => {
  return <DelphiConnectionForm {...props} forwardedRef={ref} />;
});
